import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Modal,
  Divider,
  Collapse,
  Popover,
} from "antd";
import { DeleteOutlined, PlusOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import purchaseService from "../../services/purchaseService";
import medicineService, { Medicine } from "../../services/medicineService";
import supplierService from "../../services/supplierService";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { getSuppliersStart } from "../../store/slices/supplierSlice";
import adminApi from "../../services/adminApi";
import { getPackSize, packDefaultsForCategory } from "../../utils/pack";
import { toast } from "../../utils/toast";
import PageHeader from "../../components/PageHeader";

const { Option } = Select;
const { Title, Text } = Typography;

interface LineItem {
  key: string;
  medicine?: string;
  manufacturer?: string;
  hsnCode?: string;
  batchNumber?: string;
  manufacturingDate?: any;
  expiryDate?: any;
  quantity?: number;
  freeQuantity?: number;
  unit?: string;
  packSize?: number;
  purchaseRate?: number;
  mrp?: number;
  sellingPrice?: number;
  discount?: number;
  gstRate?: number;
  rack?: string;
  shelf?: string;
  position?: string;
}

const formatLocation = (row: {
  rack?: string;
  shelf?: string;
  position?: string;
}) => {
  const parts = [row.rack, row.shelf, row.position].filter((p) =>
    String(p || "").trim()
  );
  return parts.length ? parts.join(" / ") : "";
};

const PurchaseForm: React.FC = () => {
  const [form] = Form.useForm();
  const [supplierForm] = Form.useForm();
  const [medicineForm] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { suppliers } = useSelector((s: RootState) => s.suppliers);
  const [localSuppliers, setLocalSuppliers] = useState<any[]>([]);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const emptyDraft = (): LineItem => ({
    key: "draft",
    quantity: 1,
    freeQuantity: 0,
    discount: 0,
    gstRate: 0,
  });
  const [items, setItems] = useState<LineItem[]>([]);
  const [draft, setDraft] = useState<LineItem>(emptyDraft());
  const [taxMode, setTaxMode] = useState<"cgst_sgst" | "igst">("cgst_sgst");
  const [memoType, setMemoType] = useState<"cash" | "credit">("credit");
  const [saving, setSaving] = useState(false);
  const [roundOff, setRoundOff] = useState(0);
  const [defaultGstRate, setDefaultGstRate] = useState(0);
  const [supplierModal, setSupplierModal] = useState(false);
  const [medicineModal, setMedicineModal] = useState(false);
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [creatingMedicine, setCreatingMedicine] = useState(false);

  const loadMedicines = async () => {
    const res = await medicineService.getAll({ limit: 500 });
    setMedicines(res.data.data || []);
    return res.data.data || [];
  };

  useEffect(() => {
    dispatch(getSuppliersStart({ limit: 500 }) as any);
    loadMedicines().catch(() => undefined);
    adminApi
      .getSettings()
      .then((res) => {
        const s = res.data.data;
        if (s?.taxMode) setTaxMode(s.taxMode);
        if (s?.defaultGstRate != null) {
          setDefaultGstRate(Number(s.defaultGstRate) || 0);
        }
      })
      .catch(() => undefined);
  }, [dispatch]);

  useEffect(() => {
    setLocalSuppliers(suppliers || []);
  }, [suppliers]);

  const allSuppliers = localSuppliers;

  const calcLine = (row: LineItem) => {
    const qty = row.quantity || 0;
    const rate = row.purchaseRate || 0;
    const discount = row.discount || 0;
    const gstRate = row.gstRate || 0;
    const gross = qty * rate;
    const taxable = Number((gross - (gross * discount) / 100).toFixed(2));
    const tax = Number(((taxable * gstRate) / 100).toFixed(2));
    return { taxable, tax, total: Number((taxable + tax).toFixed(2)) };
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    items.forEach((row) => {
      const line = calcLine(row);
      subtotal += line.taxable;
      totalTax += line.tax;
    });
    subtotal = Number(subtotal.toFixed(2));
    totalTax = Number(totalTax.toFixed(2));
    const grandTotal = Number(
      (subtotal + totalTax + (roundOff || 0)).toFixed(2)
    );
    return {
      subtotal,
      totalTax,
      grandTotal,
    };
  }, [items, roundOff]);

  const updateDraft = (field: keyof LineItem, value: any) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "medicine") {
        const med = medicines.find((m) => m._id === value);
        if (med) {
          next.manufacturer = med.manufacturer;
          next.hsnCode = med.hsnCode;
          next.unit = med.purchaseUnit || med.unit;
          next.packSize = med.packSize || 1;
          next.mrp = med.defaultMrp;
          next.sellingPrice = med.defaultSellingPrice || med.defaultMrp;
          next.gstRate =
            med.defaultGstRate != null ? med.defaultGstRate : defaultGstRate;
        }
      }
      return next;
    });
  };

  const addDraftToList = () => {
    if (!draft.medicine) {
      toast.error("Select a product");
      return;
    }
    if (!draft.batchNumber?.trim()) {
      toast.error("Enter batch number");
      return;
    }
    if (!draft.expiryDate) {
      toast.error("Select expiry");
      return;
    }
    if (draft.purchaseRate == null || Number.isNaN(Number(draft.purchaseRate))) {
      toast.error("Enter purchase rate");
      return;
    }
    if (draft.mrp == null || Number.isNaN(Number(draft.mrp))) {
      toast.error("Enter MRP");
      return;
    }
    if (draft.quantity == null || draft.quantity < 0) {
      toast.error("Enter a valid quantity");
      return;
    }
    setItems((rows) => [
      ...rows,
      {
        ...draft,
        key: String(Date.now()),
        gstRate: draft.gstRate ?? defaultGstRate ?? 0,
      },
    ]);
    setDraft(emptyDraft());
  };

  const updateItem = (key: string, field: keyof LineItem, value: any) => {
    setItems((rows) =>
      rows.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, [field]: value };
        if (field === "medicine") {
          const med = medicines.find((m) => m._id === value);
          if (med) {
            next.manufacturer = med.manufacturer;
            next.hsnCode = med.hsnCode;
            next.unit = med.purchaseUnit || med.unit;
            next.packSize = med.packSize || 1;
            next.mrp = med.defaultMrp;
            next.sellingPrice = med.defaultSellingPrice || med.defaultMrp;
            next.gstRate =
              med.defaultGstRate != null ? med.defaultGstRate : defaultGstRate;
          }
        }
        return next;
      })
    );
  };

  const removeRow = (key: string) => {
    setItems((rows) => rows.filter((r) => r.key !== key));
  };

  const openMedicineQuickCreate = () => {
    medicineForm.resetFields();
    medicineForm.setFieldsValue({
      category: "tablet",
      ...packDefaultsForCategory("tablet"),
      schedule: "otc",
      defaultGstRate: 12,
      reorderLevel: 100,
    });
    setMedicineModal(true);
  };

  const handleCreateSupplier = async () => {
    try {
      const values = await supplierForm.validateFields();
      setCreatingSupplier(true);
      const res = await supplierService.createSupplier({
        name: values.name,
        contactPerson: values.contactPerson || values.name,
        email: values.email,
        phone: values.phone,
        gstin: values.gstin,
        pan: values.pan,
        drugLicenseNumber: values.drugLicenseNumber,
        stateCode: values.stateCode,
        paymentTerms: "net_30",
      } as any);
      const created = res.data;
      setLocalSuppliers((prev) => {
        if (prev.some((s) => s._id === created._id)) return prev;
        return [created, ...prev];
      });
      form.setFieldsValue({ supplier: created._id });
      dispatch(getSuppliersStart({ limit: 500 }) as any);
      setSupplierModal(false);
      supplierForm.resetFields();
    } catch (e: any) {
      // API errors toasted by interceptor
    } finally {
      setCreatingSupplier(false);
    }
  };

  const handleCreateMedicine = async () => {
    try {
      const values = await medicineForm.validateFields();
      setCreatingMedicine(true);
      const code =
        values.code ||
        values.name
          .toString()
          .replace(/[^a-zA-Z0-9]/g, "")
          .toUpperCase()
          .slice(0, 12) ||
        `MED${Date.now().toString().slice(-6)}`;

      const res = await medicineService.create({
        ...values,
        code,
      });
      const created: Medicine = res.data.data;
      const list = await loadMedicines();
      const med = list.find((m: Medicine) => m._id === created._id) || created;
      setMedicines((prev) => {
        if (prev.some((m) => m._id === med._id)) return prev;
        return [med, ...prev];
      });

      setDraft((prev) => ({
        ...prev,
        medicine: med._id,
        manufacturer: med.manufacturer,
        hsnCode: med.hsnCode,
        unit: med.purchaseUnit || med.unit,
        packSize: med.packSize || 1,
        mrp: med.defaultMrp,
        sellingPrice: med.defaultSellingPrice || med.defaultMrp,
        gstRate:
          med.defaultGstRate != null ? med.defaultGstRate : defaultGstRate,
      }));

      setMedicineModal(false);
      medicineForm.resetFields();
    } catch {
      // API errors toasted by interceptor
    } finally {
      setCreatingMedicine(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!items.length) {
        toast.error("Add at least one product to the invoice");
        return;
      }

      setSaving(true);
      const payload = {
        invoiceNumber: values.invoiceNumber,
        supplier: values.supplier,
        invoiceDate: values.invoiceDate?.toISOString(),
        dueDate: values.dueDate?.toISOString(),
        taxMode,
        memoType,
        roundOff,
        totalTax: totals.totalTax,
        paymentStatus:
          values.paymentStatus ||
          (memoType === "cash" ? "paid" : "pending"),
        amountPaid:
          values.amountPaid != null
            ? values.amountPaid
            : memoType === "cash"
            ? totals.grandTotal
            : 0,
        notes: values.notes,
        items: items.map((row) => ({
          medicine: row.medicine,
          manufacturer: row.manufacturer,
          hsnCode: row.hsnCode,
          batchNumber: row.batchNumber,
          manufacturingDate: row.manufacturingDate
            ? row.manufacturingDate.toISOString()
            : undefined,
          expiryDate: row.expiryDate.endOf
            ? row.expiryDate.endOf("month").toISOString()
            : row.expiryDate.toISOString(),
          quantity: row.quantity,
          freeQuantity: row.freeQuantity || 0,
          unit: row.unit || "strips",
          packSize: row.packSize || 1,
          purchaseRate: row.purchaseRate,
          mrp: row.mrp,
          sellingPrice: row.sellingPrice ?? row.mrp,
          discount: row.discount || 0,
          gstRate: row.gstRate ?? 0,
          location: {
            rack: row.rack?.trim() || undefined,
            shelf: row.shelf?.trim() || undefined,
            position: row.position?.trim() || undefined,
          },
        })),
      };

      await purchaseService.create(payload);
      navigate("/purchases");
    } catch (e: any) {
      if (e?.errorFields?.length) {
        toast.error(
          e.errorFields[0]?.errors?.[0] ||
            "Please fill required invoice fields (invoice no. & supplier)"
        );
        form.scrollToField(e.errorFields[0].name);
        return;
      }
      // API errors toasted by interceptor
    } finally {
      setSaving(false);
    }
  };

  const draftMed = medicines.find((m) => m._id === draft.medicine);
  const draftStock = (() => {
    const pack = getPackSize({ packSize: draft.packSize ?? draftMed?.packSize });
    const packs = (draft.quantity || 0) + (draft.freeQuantity || 0);
    return `${packs * pack} ${draftMed?.unit || "pieces"}`;
  })();

  const columns = [
    {
      title: "#",
      width: 36,
      render: (_: unknown, __: LineItem, i: number) => i + 1,
    },
    {
      title: "Product",
      width: 180,
      render: (row: LineItem) => (
        <Select
          size="small"
          showSearch
          optionFilterProp="children"
          value={row.medicine}
          style={{ width: "100%" }}
          onChange={(v) => updateItem(row.key, "medicine", v)}
          filterOption={(input, option) =>
            String(option?.children ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {medicines.map((m) => (
            <Option key={m._id} value={m._id}>
              {m.code} — {m.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Batch",
      width: 90,
      render: (row: LineItem) => (
        <Input
          size="small"
          value={row.batchNumber}
          onChange={(e) => updateItem(row.key, "batchNumber", e.target.value)}
        />
      ),
    },
    {
      title: "Exp",
      width: 100,
      render: (row: LineItem) => (
        <DatePicker
          size="small"
          value={row.expiryDate}
          onChange={(d) => updateItem(row.key, "expiryDate", d)}
          style={{ width: "100%" }}
          picker="month"
          format="MM/YY"
          allowClear={false}
        />
      ),
    },
    {
      title: "Qty",
      width: 64,
      render: (row: LineItem) => (
        <InputNumber
          size="small"
          controls={false}
          min={0}
          value={row.quantity}
          onChange={(v) => updateItem(row.key, "quantity", v)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Free",
      width: 56,
      render: (row: LineItem) => (
        <InputNumber
          size="small"
          controls={false}
          min={0}
          value={row.freeQuantity}
          onChange={(v) => updateItem(row.key, "freeQuantity", v)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Rate",
      width: 80,
      render: (row: LineItem) => (
        <InputNumber
          size="small"
          controls={false}
          min={0}
          precision={2}
          value={row.purchaseRate}
          onChange={(v) => updateItem(row.key, "purchaseRate", v)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "MRP",
      width: 80,
      render: (row: LineItem) => (
        <InputNumber
          size="small"
          controls={false}
          min={0}
          precision={2}
          value={row.mrp}
          onChange={(v) => updateItem(row.key, "mrp", v)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "GST%",
      width: 64,
      render: (row: LineItem) => (
        <InputNumber
          size="small"
          controls={false}
          min={0}
          max={100}
          precision={2}
          value={row.gstRate}
          onChange={(v) => updateItem(row.key, "gstRate", v ?? 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Amt",
      width: 88,
      align: "right" as const,
      render: (row: LineItem) => `₹${calcLine(row).total.toFixed(2)}`,
    },
    {
      title: "Loc",
      width: 56,
      align: "center" as const,
      render: (row: LineItem) => {
        const label = formatLocation(row);
        return (
          <Popover
            trigger="click"
            placement="leftTop"
            title="Location (optional)"
            content={
              <Space direction="vertical" size={6} style={{ width: 200 }}>
                <Input
                  size="small"
                  placeholder="Rack"
                  value={row.rack}
                  onChange={(e) => updateItem(row.key, "rack", e.target.value)}
                />
                <Input
                  size="small"
                  placeholder="Shelf"
                  value={row.shelf}
                  onChange={(e) => updateItem(row.key, "shelf", e.target.value)}
                />
                <Input
                  size="small"
                  placeholder="Position"
                  value={row.position}
                  onChange={(e) =>
                    updateItem(row.key, "position", e.target.value)
                  }
                />
              </Space>
            }
          >
            <Button
              type="text"
              size="small"
              htmlType="button"
              icon={<EnvironmentOutlined />}
              title={label || "Set location"}
              style={{ color: label ? "#1677ff" : undefined }}
            />
          </Popover>
        );
      },
    },
    {
      title: "",
      width: 40,
      render: (row: LineItem) => (
        <Button
          type="text"
          size="small"
          danger
          htmlType="button"
          icon={<DeleteOutlined />}
          onClick={() => removeRow(row.key)}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="New Purchase Invoice"
        extra={
          <Space size={8}>
            <Button onClick={() => navigate("/purchases")}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSubmit}>
              Save & Post Stock
            </Button>
          </Space>
        }
      />
      <div className="page-panel" style={{ paddingBottom: 72 }}>
      <Form
        form={form}
        layout="vertical"
        size="small"
        initialValues={{
          invoiceDate: dayjs(),
          paymentStatus: "pending",
          amountPaid: 0,
        }}
      >
        <Row gutter={[8, 0]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="invoiceNumber"
              label="Supplier Invoice No."
              rules={[{ required: true }]}
              style={{ marginBottom: 8 }}
            >
              <Input placeholder="e.g. SDP-2479" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              label="Supplier"
              required
              style={{ marginBottom: 8 }}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="supplier"
                  noStyle
                  rules={[{ required: true, message: "Select supplier" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Select supplier"
                    style={{ width: "100%" }}
                    filterOption={(input, option) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: "4px 0" }} />
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          block
                          onClick={() => {
                            supplierForm.resetFields();
                            setSupplierModal(true);
                          }}
                        >
                          Add new supplier
                        </Button>
                      </>
                    )}
                  >
                    {allSuppliers.map((s) => (
                      <Option key={s._id} value={s._id}>
                        {s.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Button
                  icon={<PlusOutlined />}
                  title="Add supplier"
                  onClick={() => {
                    supplierForm.resetFields();
                    setSupplierModal(true);
                  }}
                />
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              rules={[{ required: true }]}
              style={{ marginBottom: 8 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Form.Item name="dueDate" label="Due Date" style={{ marginBottom: 8 }}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Form.Item label="Memo Type" style={{ marginBottom: 8 }}>
              <Select
                value={memoType}
                onChange={(v) => {
                  setMemoType(v);
                  if (v === "cash") {
                    form.setFieldsValue({
                      paymentStatus: "paid",
                      amountPaid: totals.grandTotal,
                    });
                  } else {
                    form.setFieldsValue({
                      paymentStatus: "pending",
                      amountPaid: 0,
                    });
                  }
                }}
              >
                <Option value="credit">Credit Memo</Option>
                <Option value="cash">Cash Memo</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "12px 0 8px" }}>Add product</Divider>

        <Row gutter={[8, 8]} align="bottom">
          <Col xs={24} sm={12} md={8} lg={7}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Product</div>
            <Space.Compact style={{ width: "100%" }}>
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="Select product"
                style={{ width: "calc(100% - 32px)" }}
                value={draft.medicine}
                onChange={(v) => updateDraft("medicine", v)}
                filterOption={(input, option) =>
                  String(option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: "4px 0" }} />
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      block
                      onClick={openMedicineQuickCreate}
                    >
                      Add new product
                    </Button>
                  </>
                )}
              >
                {medicines.map((m) => (
                  <Option key={m._id} value={m._id}>
                    {m.code} — {m.name}
                  </Option>
                ))}
              </Select>
              <Button
                icon={<PlusOutlined />}
                onClick={openMedicineQuickCreate}
                title="Add product"
              />
            </Space.Compact>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Batch</div>
            <Input
              value={draft.batchNumber}
              onChange={(e) => updateDraft("batchNumber", e.target.value)}
              placeholder="Batch no."
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Expiry</div>
            <DatePicker
              value={draft.expiryDate}
              onChange={(d) => updateDraft("expiryDate", d)}
              style={{ width: "100%" }}
              picker="month"
              format="MM/YY"
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Qty</div>
            <InputNumber
              controls={false}
              min={0}
              value={draft.quantity}
              onChange={(v) => updateDraft("quantity", v)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Free</div>
            <InputNumber
              controls={false}
              min={0}
              value={draft.freeQuantity}
              onChange={(v) => updateDraft("freeQuantity", v)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Pack</div>
            <InputNumber
              controls={false}
              min={1}
              value={draft.packSize ?? draftMed?.packSize ?? 1}
              onChange={(v) => updateDraft("packSize", v || 1)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Rate / pack</div>
            <InputNumber
              controls={false}
              min={0}
              precision={2}
              value={draft.purchaseRate}
              onChange={(v) => updateDraft("purchaseRate", v)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>MRP</div>
            <InputNumber
              controls={false}
              min={0}
              precision={2}
              value={draft.mrp}
              onChange={(v) => updateDraft("mrp", v)}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        <Row gutter={[8, 8]} style={{ marginTop: 8 }} align="bottom">
          <Col xs={12} sm={8} md={5} lg={5}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Manufacturer</div>
            <Input
              placeholder="Auto / edit"
              value={draft.manufacturer}
              onChange={(e) => updateDraft("manufacturer", e.target.value)}
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>HSN</div>
            <Input
              value={draft.hsnCode}
              onChange={(e) => updateDraft("hsnCode", e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Mfg Date</div>
            <DatePicker
              value={draft.manufacturingDate}
              onChange={(d) => updateDraft("manufacturingDate", d)}
              style={{ width: "100%" }}
              placeholder="Optional"
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Sale</div>
            <InputNumber
              controls={false}
              min={0}
              precision={2}
              value={draft.sellingPrice}
              onChange={(v) => updateDraft("sellingPrice", v)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Disc%</div>
            <InputNumber
              controls={false}
              min={0}
              max={100}
              value={draft.discount}
              onChange={(v) => updateDraft("discount", v)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={8} sm={4} md={3} lg={2}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>GST%</div>
            <InputNumber
              controls={false}
              min={0}
              max={100}
              precision={2}
              value={draft.gstRate}
              onChange={(v) => updateDraft("gstRate", v ?? 0)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Space wrap>
              <Button
                type="primary"
                htmlType="button"
                icon={<PlusOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addDraftToList();
                }}
              >
                Add
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                → Stock {draftStock} · ₹{calcLine(draft).total.toFixed(2)}
                {formatLocation(draft) ? ` · ${formatLocation(draft)}` : ""}
              </Text>
            </Space>
          </Col>
        </Row>

        <Collapse
          ghost
          size="small"
          style={{ marginTop: 4 }}
          items={[
            {
              key: "location",
              label: (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Location (optional) — Rack / Shelf / Position
                  {formatLocation(draft) ? `: ${formatLocation(draft)}` : ""}
                </Text>
              ),
              children: (
                <Row gutter={[8, 8]}>
                  <Col xs={8} sm={6} md={4}>
                    <Input
                      size="small"
                      placeholder="Rack"
                      value={draft.rack}
                      onChange={(e) => updateDraft("rack", e.target.value)}
                    />
                  </Col>
                  <Col xs={8} sm={6} md={4}>
                    <Input
                      size="small"
                      placeholder="Shelf"
                      value={draft.shelf}
                      onChange={(e) => updateDraft("shelf", e.target.value)}
                    />
                  </Col>
                  <Col xs={8} sm={6} md={4}>
                    <Input
                      size="small"
                      placeholder="Position"
                      value={draft.position}
                      onChange={(e) => updateDraft("position", e.target.value)}
                    />
                  </Col>
                </Row>
              ),
            },
          ]}
        />

        <Table
          size="small"
          style={{ marginTop: 16 }}
          pagination={false}
          rowKey="key"
          dataSource={items}
          columns={columns}
          locale={{ emptyText: "No products added yet — fill the form above and click Add" }}
        />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col span={10}>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Row justify="space-between">
              <Text>Taxable Amt</Text>
              <Text>₹{totals.subtotal.toFixed(2)}</Text>
            </Row>
            <Row justify="space-between">
              <Text>GST (from lines)</Text>
              <Text>₹{totals.totalTax.toFixed(2)}</Text>
            </Row>
            <Row justify="space-between" align="middle">
              <Text>Round Off</Text>
              <InputNumber
                value={roundOff}
                onChange={(v) => setRoundOff(v || 0)}
                step={0.01}
                controls={false}
              />
            </Row>
            <Row justify="space-between">
              <Title level={5} style={{ margin: 0 }}>
                Net Amount
              </Title>
              <Title level={5} style={{ margin: 0 }}>
                ₹{totals.grandTotal.toFixed(2)}
              </Title>
            </Row>
            <Space wrap>
              <Form.Item
                name="paymentStatus"
                label="Payment"
                style={{ marginBottom: 0 }}
              >
                <Select style={{ width: 140 }}>
                  <Option value="pending">Pending</Option>
                  <Option value="partially_paid">Partial</Option>
                  <Option value="paid">Paid</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="amountPaid"
                label="Paid ₹"
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={0} style={{ width: 120 }} />
              </Form.Item>
            </Space>
          </Space>
        </Col>
      </Row>

      <div
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 20,
          marginTop: 16,
          marginLeft: -12,
          marginRight: -12,
          marginBottom: -12,
          padding: "10px 12px",
          background: "#fff",
          borderTop: "1px solid #e8ecf0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          boxShadow: "0 -2px 8px rgba(15,23,42,0.06)",
        }}
      >
        <Button onClick={() => navigate("/purchases")}>Cancel</Button>
        <Button
          type="primary"
          htmlType="button"
          loading={saving}
          onClick={handleSubmit}
        >
          Save & Post Stock
        </Button>
      </div>
      </Form>
      </div>

      <Modal
        title="Quick Add Supplier"
        open={supplierModal}
        onCancel={() => setSupplierModal(false)}
        onOk={handleCreateSupplier}
        confirmLoading={creatingSupplier}
        okText="Create & Select"
        destroyOnClose
      >
        <Form form={supplierForm} layout="vertical">
          <Form.Item
            name="name"
            label="Supplier Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. Shri Datta Pharma" />
          </Form.Item>
          <Form.Item name="contactPerson" label="Contact Person">
            <Input placeholder="Defaults to supplier name if empty" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: "Phone is required" }]}
          >
            <Input placeholder="Phone number" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, type: "email", message: "Valid email required" },
            ]}
          >
            <Input placeholder="email@example.com" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="gstin" label="GSTIN">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pan" label="PAN">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="drugLicenseNumber" label="Drug License">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="stateCode" label="State Code">
                <Input placeholder="e.g. 27" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Quick Add Product"
        open={medicineModal}
        onCancel={() => {
          setMedicineModal(false);
        }}
        onOk={handleCreateMedicine}
        confirmLoading={creatingMedicine}
        okText="Create & Select"
        width={640}
        destroyOnClose
      >
        <Form form={medicineForm} layout="vertical">
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="code"
                label="Code / SKU"
                tooltip="Leave blank to auto-generate from name"
              >
                <Input placeholder="Auto if empty" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="name"
                label="Product Name"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <Input placeholder="e.g. AZICIP 500 TAB" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="manufacturer"
                label="Manufacturer"
                rules={[{ required: true, message: "Manufacturer required" }]}
              >
                <Input placeholder="e.g. CIPLA" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="genericName" label="Generic Name">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={(cat) =>
                    medicineForm.setFieldsValue(packDefaultsForCategory(cat))
                  }
                >
                  {[
                    "tablet",
                    "capsule",
                    "syrup",
                    "injection",
                    "cream",
                    "drops",
                    "inhaler",
                    "other",
                  ].map((c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="unit"
                label="Stock Unit"
                rules={[{ required: true }]}
              >
                <Select>
                  {["pieces", "bottles", "boxes", "strips", "vials"].map((u) => (
                    <Option key={u} value={u}>
                      {u}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="purchaseUnit"
                label="Purchase Unit"
                rules={[{ required: true }]}
              >
                <Select>
                  {["pieces", "bottles", "boxes", "strips", "vials"].map((u) => (
                    <Option key={u} value={u}>
                      {u}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="packSize"
                label="Pack Size"
                rules={[{ required: true }]}
                tooltip="Tablets per strip, etc."
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="hsnCode" label="HSN">
                <Input placeholder="e.g. 3004" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="defaultGstRate" label="GST %">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="defaultMrp" label="MRP / pack">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="defaultSellingPrice" label="Sale / pack">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="reorderLevel" label="Reorder (stock units)">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="schedule" label="Schedule" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PurchaseForm;
